import React, { useMemo, useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import { enqueueSnackbar } from "notistack";
import {
  getComplianceFrameworkBadgeText,
  getComplianceIcon,
} from "../../../../assets/images/compliance/IconCompliance.jsx";
import { postAccountCompliance } from "../../../../helpers/auth_aws_helper";
import { getApiErrorMessage } from "../../../../helpers/aws_api_helper";
import {
  getListOfAccts,
  getListOfOrgsAndAccts,
} from "../../../../store/actions";
import {
  EditAccountSortableHeader,
  EditAccountTablePagination,
  toggleSort,
} from "../../editAccountTableControls";
import { TruncatedTooltipText } from "../../editAccountTooltipStyles";
import AddonStatusChip from "../../AddonStatusChip";

const FRAMEWORKS_DESCRIPTION =
  "Reduce the burden of manual audits and continuously monitor your cloud account’s compliance with various regulations and standards. To enable compliance packages, send a request to CloudCatcher support to enable.";

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.5,
  px: 2,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.5,
  px: 2,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

const formatMetaDate = (dateInput) => {
  if (!dateInput || dateInput === "null") return null;
  if (typeof dateInput === "object" && dateInput.date) {
    const { year, month, day } = dateInput.date;
    const dt = new Date(year, month - 1, day);
    if (isNaN(dt.getTime())) return null;
    return dt.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const dt = new Date(dateInput);
  if (isNaN(dt.getTime())) return String(dateInput);
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getAddonTimestamp = (addon) =>
  addon?.enabledDate ||
  addon?.enabledOn ||
  addon?.addedOn ||
  addon?.createdDate ||
  addon?.lastModifiedDate ||
  null;

const getAddonActor = (addon) =>
  addon?.enabledBy ||
  addon?.addedBy ||
  addon?.createdBy ||
  addon?.lastModifiedBy ||
  null;

const buildLastEnabledMeta = (addons = []) => {
  const enabled = addons.filter((addon) => addon?.enabled);
  if (!enabled.length) return null;

  const withTimestamp = enabled
    .map((addon) => ({
      name: addon.name,
      by: getAddonActor(addon),
      on: getAddonTimestamp(addon),
      sortValue: getAddonTimestamp(addon)
        ? new Date(getAddonTimestamp(addon)).getTime()
        : 0,
    }))
    .filter((item) => item.on);

  if (!withTimestamp.length) return null;

  withTimestamp.sort((a, b) => b.sortValue - a.sortValue);
  const latest = withTimestamp[0];
  return {
    name: latest.name,
    by: latest.by || null,
    on: formatMetaDate(latest.on),
  };
};

const FrameworkLogo = ({ name }) => {
  const badgeText = getComplianceFrameworkBadgeText(name);
  const iconSrc = getComplianceIcon(name);

  if (badgeText) {
    return (
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "6px",
          backgroundColor: "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.6rem",
            fontWeight: 700,
            color: "#475569",
            lineHeight: 1,
          }}
        >
          {badgeText}
        </Typography>
      </Box>
    );
  }

  if (iconSrc) {
    return (
      <Box
        component="img"
        src={iconSrc}
        alt=""
        sx={{
          width: 28,
          height: 28,
          objectFit: "contain",
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "6px",
        backgroundColor: "#e2e8f0",
        flexShrink: 0,
      }}
    />
  );
};

const ComplianceAddons = (props) => {
  const [enablingName, setEnablingName] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  const complianceRows = useMemo(() => {
    if (!props.complianceAddOns) return [];
    return props.complianceAddOns.map((addOn, index) => ({
      id: index + 1,
      name: addOn.name,
      price: addOn.price,
      description: addOn.description,
      enabled: !!addOn.enabled,
      raw: addOn,
    }));
  }, [props.complianceAddOns]);

  const activeCount = complianceRows.filter((row) => row.enabled).length;
  const totalCount = complianceRows.length;
  const lastEnabledMeta = useMemo(
    () => buildLastEnabledMeta(props.complianceAddOns || []),
    [props.complianceAddOns]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return complianceRows;
    return complianceRows.filter((row) => {
      const haystack = [row.name, row.description]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [complianceRows, search]);

  const sortedRows = useMemo(() => {
    const rows = [...filteredRows];
    rows.sort((a, b) => {
      let cmp = 0;
      switch (orderBy) {
        case "status":
          cmp = Number(a.enabled) - Number(b.enabled);
          break;
        case "description":
          cmp = (a.description || "").localeCompare(b.description || "");
          break;
        case "name":
        default:
          cmp = a.name.localeCompare(b.name);
          break;
      }
      return order === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [filteredRows, orderBy, order]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage) || 1);

  useEffect(() => {
    if (page > pageCount - 1) setPage(0);
  }, [page, pageCount]);

  const pagedRows = sortedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSort = (field) => {
    toggleSort(orderBy, order, field, setOrderBy, setOrder);
  };

  const handleEnableCompliance = async (row) => {
    const editAccount = props.editAccount;
    if (!editAccount?.accountNumber || !row?.name || enablingName) return;

    const orgId = editAccount?.orgId;
    if (orgId == null || orgId === "") {
      enqueueSnackbar(
        "Unable to enable compliance: organization ID is missing for this account.",
        { variant: "error" }
      );
      return;
    }

    setEnablingName(row.name);
    const payload = {
      orgId: String(orgId),
      accountNumber: editAccount.accountNumber,
      complianceAddOns: [{ name: row.name, enabled: true }],
    };

    try {
      const response = await postAccountCompliance(payload);
      if (response && response.status === 200) {
        enqueueSnackbar(`${row.name} request submitted successfully.`, {
          variant: "success",
        });
        props.getListOfAccts();
        props.getListOfOrgsAndAccts();
      } else {
        enqueueSnackbar(
          getApiErrorMessage(response?.data) ||
            "Failed to enable compliance framework.",
          { variant: "error" }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(error?.response?.data) ||
          error?.message ||
          "Failed to enable compliance framework.",
        { variant: "error" }
      );
    } finally {
      setEnablingName(null);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        ...(props.isAzureAccount && {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }),
      }}
    >
      <Box
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: "12px",
          p: 2.5,
          backgroundColor: "#f3f4f6",
          display: "flex",
          flexDirection: "column",
          ...(props.isAzureAccount && { flex: 1, minHeight: 0 }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 2,
            pb: 1.25,
            mb: 1.25,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Typography
            sx={{ fontWeight: 700, color: "#111827", fontSize: "1rem" }}
          >
            Compliance Frameworks
          </Typography>
          <AddonStatusChip
            active={activeCount > 0}
            label={
              activeCount > 0
                ? `Active · ${activeCount}/${totalCount}`
                : `Inactive · ${activeCount}/${totalCount}`
            }
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 1.25,
          }}
        >
          <Typography
            sx={{
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 400,
              lineHeight: 1.6,
              flex: 1,
              width: "100%",
              minWidth: { xs: "100%", sm: 240 },
            }}
          >
            {FRAMEWORKS_DESCRIPTION}
          </Typography>
          <TextField
            size="small"
            placeholder="Search Frameworks"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 280 },
              maxWidth: 360,
              flexShrink: 0,
              "& .MuiOutlinedInput-root": {
                fontSize: "0.875rem",
                backgroundColor: "#fff",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": {
                  borderColor: "#111827",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": { py: "8px" },
            }}
          />
        </Box>

        {lastEnabledMeta && (
          <Box
            sx={{
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              px: 1.5,
              py: 1,
              mb: 2,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 2.5,
              rowGap: 0.75,
            }}
          >
            {[
              {
                label: "Last framework enabled",
                value: lastEnabledMeta.name,
              },
              lastEnabledMeta.by
                ? { label: "Added by", value: lastEnabledMeta.by }
                : null,
              lastEnabledMeta.on
                ? { label: "Added on", value: lastEnabledMeta.on }
                : null,
            ]
              .filter(Boolean)
              .map((item, index) => (
                <React.Fragment key={item.label}>
                  {index > 0 && (
                    <Box
                      component="span"
                      aria-hidden
                      sx={{
                        width: 3,
                        height: 3,
                        borderRadius: "50%",
                        backgroundColor: "#94a3b8",
                        flexShrink: 0,
                        display: { xs: "none", sm: "inline-block" },
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 400,
                      color: "#64748b",
                      lineHeight: 1.4,
                    }}
                  >
                    {item.label}: {item.value}
                  </Typography>
                </React.Fragment>
              ))}
          </Box>
        )}

        <Box
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            backgroundColor: "#fff",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            ...(props.isAzureAccount && { flex: 1 }),
          }}
        >
          <TableContainer
            sx={{
              width: "100%",
              maxWidth: "100%",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Table
              size="small"
              sx={{ width: "100%", minWidth: 720, tableLayout: "fixed" }}
            >
              <colgroup>
                <col style={{ width: "32%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "50%" }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <EditAccountSortableHeader
                    field="name"
                    orderBy={orderBy}
                    order={order}
                    onSort={handleSort}
                    align="left"
                    headerCellSx={HEADER_CELL_SX}
                  >
                    Compliance
                  </EditAccountSortableHeader>
                  <EditAccountSortableHeader
                    field="status"
                    orderBy={orderBy}
                    order={order}
                    onSort={handleSort}
                    align="center"
                    headerCellSx={HEADER_CELL_SX}
                  >
                    Status
                  </EditAccountSortableHeader>
                  <EditAccountSortableHeader
                    field="description"
                    orderBy={orderBy}
                    order={order}
                    onSort={handleSort}
                    align="left"
                    headerCellSx={HEADER_CELL_SX}
                  >
                    Description
                  </EditAccountSortableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                  >
                    <TableCell align="left" sx={BODY_CELL_SX}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          minWidth: 0,
                        }}
                      >
                        <FrameworkLogo name={row.name} />
                        <TruncatedTooltipText title={row.name} sx={CELL_TEXT}>
                          {row.name}
                        </TruncatedTooltipText>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ ...BODY_CELL_SX, px: 2.5 }}>
                      {row.enabled ? (
                        <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 22 }} />
                      ) : (
                        <Button
                          variant="contained"
                          disabled={!!enablingName}
                          onClick={() => handleEnableCompliance(row)}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            borderRadius: "8px",
                            px: 1.5,
                            py: 0.25,
                            minWidth: 72,
                            boxShadow: "none",
                            color: "#FFFFFF",
                            backgroundColor: "#F98125",
                            border: "none",
                            "&:hover": {
                              backgroundColor: "#e8741c",
                              boxShadow: "none",
                            },
                            "&.Mui-disabled": {
                              color: "#FFFFFF",
                              backgroundColor: "#F98125",
                              opacity: 0.5,
                            },
                          }}
                        >
                          {enablingName === row.name ? (
                            <CircularProgress
                              size={14}
                              sx={{ color: "#fff" }}
                            />
                          ) : (
                            "Request"
                          )}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell align="left" sx={BODY_CELL_SX}>
                      <TruncatedTooltipText
                        title={row.description || ""}
                        sx={{
                          ...CELL_TEXT,
                          fontWeight: 400,
                          color: "#64748b",
                        }}
                      >
                        {row.description || "—"}
                      </TruncatedTooltipText>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <EditAccountTablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={sortedRows.length}
            onPageChange={setPage}
            onRowsPerPageChange={(value) => {
              setRowsPerPage(value);
              setPage(0);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  const { accounts, organizations, reg_acct_error, account, loading, message } =
    state.orgAccount;
  return {
    accounts,
    organizations,
    reg_acct_error,
    account,
    loading,
    message,
    orgLicensing: state.licensing,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getListOfAccts: (payload) => {
      dispatch(getListOfAccts(payload));
    },
    getListOfOrgsAndAccts: () => {
      dispatch(getListOfOrgsAndAccts());
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ComplianceAddons);
